import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { Panel, PanelHeader, PanelBody } from '../../components/panel/panel.jsx';
import axiosInstance from '../../config/axiosApi/axiosApi.jsx';
import APIs from '../../Constants/apis.js';
import Preloader from '../../components/CustomComponents/preLoad/preLoader.js';
import showErrorToast from '../../components/CustomComponents/toast/toast.js';
import Pagination from '../../components/CustomComponents/Pagination/pagination.js';
import PageSizeDropDownSelection from '../../components/CustomComponents/DropdownSelections/DropDownSelections.jsx';
import { logout } from '../../app/auth/authSlice.js';
import useDarkMode from '../../hooks/useDarkMode';
import { REVENUE_COMPANY_FILTERS } from './revenueConstants.js';

const companyField = (company, key) =>
  company?.[key] ?? company?.[key.charAt(0).toUpperCase() + key.slice(1)];

function RevenueCompanies() {
  const [searchParams] = useSearchParams();
  const filter = searchParams.get('filter') || 'package_running';
  const filterMeta = REVENUE_COMPANY_FILTERS[filter] || REVENUE_COMPANY_FILTERS.package_running;

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isDarkMode = useDarkMode();

  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState({ value: '50', label: '50' });
  const [pageNumber, setPageNumber] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchCompanies = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_COMPANIES, {
        params: {
          SortBy: 'CompID',
          SortOrder: 'DESC',
          Search: search.trim() || '',
          PageSize: pageSize.value,
          PageNumber: pageNumber,
          onlyParentCompanies: true,
          packageFilter: filter,
        },
      });

      if (response.data.status) {
        const companyData = response.data.data?.companies ?? response.data.data?.Companies ?? [];
        setCompanies(Array.isArray(companyData) ? companyData : []);
        const totalRecs =
          response.data.totalRecords ||
          response.data.data?.totalRecord ||
          response.data.data?.TotalRecord ||
          companyData.length;
        const totalPgs =
          response.data.totalPages ||
          response.data.data?.pageCount ||
          response.data.data?.PageCount ||
          Math.ceil(totalRecs / parseInt(pageSize.value, 10));
        setTotalRecords(totalRecs);
        setTotalPages(totalPgs);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        dispatch(logout());
        setTimeout(() => navigate('/user/login', { replace: true }), 1000);
      } else {
        showErrorToast('Failed to load companies');
      }
    } finally {
      setLoading(false);
    }
  }, [dispatch, navigate, search, pageSize.value, pageNumber, filter]);

  useEffect(() => {
    setPageNumber(1);
  }, [filter, search]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const isAccessActive = (company) => {
    const raw = companyField(company, 'compExpireDate');
    if (!raw) return false;
    const d = moment(raw);
    return d.isValid() && d.year() >= 2000 && d.isSameOrAfter(moment(), 'day');
  };

  const renderPackageCell = (company) => {
    const name = companyField(company, 'packageName');
    if (name) {
      return <span className="badge bg-info">{name}</span>;
    }
    return <span className="text-muted">—</span>;
  };

  const renderAccessCell = (company) => {
    const active = isAccessActive(company);
    const raw = companyField(company, 'compExpireDate');
    return (
      <div className="d-flex flex-column gap-1">
        <span className={`badge ${active ? 'bg-success' : 'bg-danger'}`}>
          {active ? 'Running' : 'Expired'}
        </span>
        {raw && moment(raw).isValid() && (
          <small className="text-muted">{moment(raw).format('MMM D, YYYY')}</small>
        )}
      </div>
    );
  };

  return (
    <div>
      {loading && <Preloader />}
      <ol className="breadcrumb float-xl-end">
        <li className="breadcrumb-item"><Link to="/dashboard">Home</Link></li>
        <li className="breadcrumb-item"><Link to="/revenue">Revenue</Link></li>
        <li className="breadcrumb-item active">Companies</li>
      </ol>
      <h1 className="page-header">
        {filterMeta.title}
        <small>{filterMeta.description}</small>
      </h1>

      <div className="mb-3 d-flex flex-wrap gap-2">
        {Object.entries(REVENUE_COMPANY_FILTERS).map(([key, meta]) => (
          <Link
            key={key}
            to={`/revenue/companies?filter=${key}`}
            className={`btn btn-sm ${filter === key ? 'btn-primary' : 'btn-outline-secondary'}`}
          >
            {meta.title}
          </Link>
        ))}
      </div>

      <Panel>
        <PanelHeader>
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 w-100">
            <span>
              {totalRecords} {totalRecords === 1 ? 'company' : 'companies'}
            </span>
            <div className="d-flex gap-2 align-items-center">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search name, email, phone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ minWidth: '220px' }}
              />
              <PageSizeDropDownSelection
                defaultValue={pageSize}
                onChange={(opt) => {
                  setPageSize(opt);
                  setPageNumber(1);
                }}
              />
            </div>
          </div>
        </PanelHeader>
        <PanelBody>
          <div className="table-responsive">
            <table className="table table-hover text-nowrap">
              <thead
                style={{
                  backgroundColor: isDarkMode ? '#2d3436' : '#f8f9fa',
                }}
              >
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>City</th>
                  <th>Currency</th>
                  <th>Package</th>
                  <th>Access</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {companies.length > 0 ? (
                  companies.map((company) => {
                    const compID = companyField(company, 'compID');
                    return (
                      <tr key={compID}>
                        <td>{compID}</td>
                        <td>{companyField(company, 'compName')}</td>
                        <td>{companyField(company, 'compEmail')}</td>
                        <td>{companyField(company, 'compPhone')}</td>
                        <td>{companyField(company, 'compCity')}</td>
                        <td>{companyField(company, 'compCurrency')}</td>
                        <td>{renderPackageCell(company)}</td>
                        <td>{renderAccessCell(company)}</td>
                        <td>
                          <Link
                            to={`/company/updateCompany/${compID}`}
                            className="btn btn-sm btn-outline-primary"
                          >
                            <i className="fa fa-edit" />
                          </Link>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={9} className="text-center text-muted py-4">
                      No companies match this filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <Pagination
              totalPages={totalPages}
              currentPage={pageNumber}
              onPageChange={setPageNumber}
            />
          )}
        </PanelBody>
      </Panel>
    </div>
  );
}

export default RevenueCompanies;
